import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "The PTR or \"pointer\" DNS record type maps an IP address to a domain name in the DNS. This is called a DNS reverse lookup.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, "Most DNS records types are used in \"forward lookups\". A DNS forward lookup maps a DNS name to an IP address or another piece of DNS data. PTR records do the opposite. A reverse lookup for a PTR record maps an IP address to a DNS name.", -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The PTR record type was introduced in the original DNS specifications ("),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1034",
    rel: "noopener noreferrer"
  }, "RFC 1034"),
  /*#__PURE__*/_createTextVNode(" and "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1035",
    rel: "noopener noreferrer"
  }, "1035"),
  /*#__PURE__*/_createTextVNode(") in 1987. Today, "),
  /*#__PURE__*/_createElementVNode("strong", null, "PTR records are essential for email delivery."),
  /*#__PURE__*/_createTextVNode(" They are used as a layer of security to prove that a mail server is trustworthy.")
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/dns-record-types/ptr/the-dns-ptr-record-type.jpg",
    alt: "Pointer records in the DNS",
    title: "Pointer records in the DNS",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Pointer records in the DNS. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h2", null, "PTR record format", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", null, "The PTR record format is straightforward but constructing PTR names requires a few steps. The data of each PTR record contains only one data field. This field gives the canonical name for the IP address. Like all other records in the DNS, each PTR record also has a Time-to-Live (TTL) value in seconds.", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Here is an example of a PTR record that maps the IPv4 address "),
  /*#__PURE__*/_createElementVNode("code", null, "10.0.0.1"),
  /*#__PURE__*/_createTextVNode(" to the DNS name "),
  /*#__PURE__*/_createElementVNode("code", null, "mailserver.example.org"),
  /*#__PURE__*/_createTextVNode(". This record has a TTL of one hour (3600 seconds).")
], -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("pre", null, "1.0.0.10.in-addr.arpa. 3600 PTR mailserver.example.org.", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("h2", null, "PTR record names", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The owner name of a PTR record always lies in the .arpa zone. The .arpa Top Level Domain was the first TLD created. It was proposed in 1983 by "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc881",
    rel: "noopener noreferrer"
  }, "RFC 881"),
  /*#__PURE__*/_createTextVNode(". For IPv4 addresses, the rightmost labels are always \"in-addr.arpa\". For IPv6 addresses, \"ip6.arpa\" is used.")
], -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, "Preceding \"in-addr.arpa\" or \"ip6.arpa\" are DNS labels that contain the IP address in a specific format. For IPv4, there are four labels for the address, one for each octet in the IPv4 address. For IPv6, there is one label for each hex digit in the IPv6 address. The address labels appear in reverse order. The least significant labels of the address come first.", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, "IPv6 reverse lookup names are long! They contain 32 labels for the hex digits of the address plus two more labels for \"ip6.arpa\". So there are 34 labels in the owner name of every IPv6 PTR record versus six labels for IPv4 PTR records.", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("h3", null, "IPv4 PTR record names", -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, "Formulate the name of a PTR record for an IPv4 address using these steps:", -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Separate the IPv4 address into four labels. For "),
    /*#__PURE__*/_createElementVNode("code", null, "10.0.0.1"),
    /*#__PURE__*/_createTextVNode(" the labels are 10, 0, 0, and 1.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Reverse the labels and join them with dots: "),
    /*#__PURE__*/_createElementVNode("code", null, "1.0.0.10"),
    /*#__PURE__*/_createTextVNode(".")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Add \"in-addr.arpa\" to finish the lookup name: "),
    /*#__PURE__*/_createElementVNode("code", null, "1.0.0.10.in-addr.arpa"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("h3", null, "IPv6 PTR record names", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("code", null, "2001:1000::12ab", -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Expand the double colon if present. For "),
    /*#__PURE__*/_createElementVNode("code", null, "2001:1000::12ab"),
    /*#__PURE__*/_createTextVNode(" we get "),
    /*#__PURE__*/_createElementVNode("code", null, "2001:1000:0000:0000:0000:0000:0000:12ab"),
    /*#__PURE__*/_createTextVNode(".")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Remove colons: "),
    /*#__PURE__*/_createElementVNode("code", null, "200110000000000000000000000012ab")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Reverse the hex digits: "),
    /*#__PURE__*/_createElementVNode("code", null, "ba210000000000000000000000011002")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Insert a dot between each hex digit: "),
    /*#__PURE__*/_createElementVNode("code", null, "b.a.2.1.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.1.1.0.0.2")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Add \"ip6.arpa\" to finish the lookup name: "),
    /*#__PURE__*/_createElementVNode("code", null, "b.a.2.1.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.1.1.0.0.2.ip6.arpa"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", null, "DNS lookup names are case insensitive. Upper-case or lower-case characters can be used for the hex digit labels. Both will give identical results.", -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("p", null, "The PTR record for this address would be:", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("pre", null, "b.a.2.1.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.0.1.1.0.0.2.ip6.arpa. 3600 PTR mailserver.example.org.", -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("p", null, "While two consecutive colons are a shortcut for a long series of zeros in an IPv6 address, there is no similar shortcut for the PTR name. All IPv6 PTR record owner names contain 34 labels.", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("h2", null, "Forward-confirmed reverse DNS", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, "The complex-sounding term \"forward-confirmed reverse DNS\" (FCrDNS) means a pair of forward and reverse lookup records in the DNS that agree with one another.", -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("p", null, "The forward lookup record is of type A or AAAA. The reverse lookup record is of type PTR. The owner name of the A or AAAA record must match the canonical name in the PTR record data. And the address in the A or AAAA record must match the owner name of the PTR record.", -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("p", null, "These two example records form a forward-confirmed reverse DNS pair:", -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("pre", null, "; In the example.org zone:\nmailserver.example.org. 3600 A 10.0.0.1\n\n; In the reverse lookup zone 10.in-addr.arpa:\n1.0.0.10.in-addr.arpa. 3600 PTR mailserver.example.org.", -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("p", null, "Forward-confirmed reverse DNS also applies to IPv6 using a AAAA record and a corresponding 34-label PTR record.", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("p", null, "FCrDNS is a basic but effective security measure because the two records that comprise a FCrDNS pair are in different zones in the DNS. It is difficult for spammers using temporary mail servers to pass the FCrDNS test.", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("p", null, "It's not required that every A or AAAA record on the Internet have a matching PTR record. Most DNS names do not have PTR records but in some contexts, such as email delivery, the existence of PTR records is critical.", -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "The .arpa TLD contains all of the PTR records for the Internet. The .arpa TLD is managed by the Internet Assigned Numbers Authority (IANA) with guidance from the Internet Architecture Board (IAB). ")
], -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("p", null, "Walking down the zone tree from the .arpa TLD through delegations, you will arrive at a reverse lookup zone that contains PTR records. This zone will be managed by the agency, ISP, or corporation that purchased that block of IP addresses.", -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("p", null, "Cloud providers offering mail hosting services will create PTR records in their reverse lookup zones automatically or at the request of their customers. But PTR record creation is not usually offered by cloud providers for other services.", -1)
const _hoisted_34 = /*#__PURE__*/_createElementVNode("p", null, "For typical home Internet scenarios, the router's public IP is from a block of IP addresses owned by the ISP. Consumer ISPs typically will not create PTR records for customers. Business ISPs may offer this service to customers who run their own mail servers. Contact your ISP support team to find out if PTR record creation is supported.", -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("See the "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://www.iana.org/domains/arpa",
    rel: "noopener noreferrer"
  }, "IANA page on .arpa zone management"),
  /*#__PURE__*/_createTextVNode(" for more information on the .arpa TLD.")
], -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("h2", null, "Improving email delivery with PTR records", -1)
const _hoisted_37 = /*#__PURE__*/_createElementVNode("p", null, "PTR records were intended for informational and inventory purposes but they have become a vital tool in the never-ending fight against email spam.", -1)
const _hoisted_38 = /*#__PURE__*/_createElementVNode("p", null, "Virtually all email relays require that the sending server pass a FCrDNS check. A PTR record must exist with the same name and address information as the type A or AAAA record for the mail server name. Mail from the sending server will usually be treated as spam if the PTR record does not exist or does not match.", -1)
const _hoisted_39 = /*#__PURE__*/_createElementVNode("h2", null, "Looking up PTR records for an IP address", -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("p", null, "The dig or nslookup command line tools can find the DNS name for an IP address. Dig has a special command line option \"-x\" that automatically uses the rules above to convert an IP address to a PTR lookup name. To find the PTR records for Cloudflare's public DNS resolver address:", -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("pre", null, "dig -x 1.1.1.1", -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("p", null, "On operating systems that support nslookup, you can use the following:", -1)
const _hoisted_43 = /*#__PURE__*/_createElementVNode("pre", null, "nslookup -type=PTR 1.1.1.1", -1)
const _hoisted_44 = /*#__PURE__*/_createElementVNode("p", null, "You can also explicitly look up the reverse lookup name using dig if you wish. Dig will perform the same DNS lookup as with the \"-x\" option above if you use:", -1)
const _hoisted_45 = /*#__PURE__*/_createElementVNode("pre", null, "dig ptr 1.1.1.1.in-addr.arpa", -1)
const _hoisted_46 = {
  class: "not-prose text-base mx-auto max-w-lg",
  autocomplete: "off"
}
const _hoisted_47 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_48 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_49 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_50 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " What TTL should you use for PTR records? ", -1)
const _hoisted_51 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_52 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "A TTL of one hour or more is recommended for PTR records because they tend to be relatively static.")
], -1)
const _hoisted_53 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Can an IP have multiple PTR records? ", -1)
const _hoisted_54 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_55 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "The DNS specifications do not prohibit multiple PTR records. However, this is not recommended. The PTR record identifies the canonical DNS name associated with an IP address. By definition, this is a singular name.")
], -1)
const _hoisted_56 = /*#__PURE__*/_createElementVNode("p", null, "Mail servers performing FCrDNS checks are not guaranteed to examine more than one PTR record. Mail delivery may fail or be intermittent if multiple PTR records exist.", -1)
const _hoisted_57 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " How do I set up a PTR record? ", -1)
const _hoisted_58 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_59 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "If a hosting provider operates your mail server, check your provider's documentation for instructions on setting up a PTR record. Most hosting providers will have a checkbox in their web interface to enable PTR creation. They may even do this for you automatically since PTR records for mail servers are critical.")
], -1)
const _hoisted_60 = /*#__PURE__*/_createElementVNode("p", null, "Creating a PTR can be difficult if you operate your mail server and DNS forward lookup zone yourself. Your ISP owns the reverse lookup zone for your IP address space. Contact your ISP's technical support and ask them to add your PTR records or delegate a reverse lookup zone to you.", -1)
const _hoisted_61 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Are matching PTR records always necessary? ", -1)
const _hoisted_62 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_63 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "For mail servers, matching forward and reverse lookup records are virtually mandatory. However, for most other applications, PTR records are optional.")
], -1)
const _hoisted_64 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("PTR records are often omitted or do not match for web servers and other applications. If you resolve "),
  /*#__PURE__*/_createElementVNode("code", null, "www.google.com"),
  /*#__PURE__*/_createTextVNode(" and perform a reverse lookup on the IP address, you will probably see a cryptic name in the PTR. This cryptic name is the hostname of a Google server in a data center close to you. For example:")
], -1)
const _hoisted_65 = /*#__PURE__*/_createElementVNode("pre", null, "dig +short www.google.com\n142.251.215.228\n\ndig +short -x 142.251.215.228\nsea09s35-in-f4.1e100.net.", -1)
const _hoisted_66 = /*#__PURE__*/_createElementVNode("p", null, "Google's web servers fail the forward-confirmed reverse DNS test. This does not cause problems because web servers and clients do not perform FCrDNS checks. But you can be sure that all of Google's mail servers have PTR records that will satisfy FCrDNS checks.", -1)
const _hoisted_67 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " What does ARPA stand for? ", -1)
const _hoisted_68 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_69 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "PTR records appear under the .arpa TLD. This was originally short for the Advanced Research Projects Agency, but the acronym has since been redefined.")
], -1)
const _hoisted_70 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The .arpa TLD was the very first TLD created during the early days of the Internet. It is still used today for all PTR records. IPv4 PTR records appear in the DNS under "),
  /*#__PURE__*/_createElementVNode("code", null, "in-addr.arpa"),
  /*#__PURE__*/_createTextVNode(". IPv6 PTR records use "),
  /*#__PURE__*/_createElementVNode("code", null, "ip6.arpa"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_71 = /*#__PURE__*/_createElementVNode("p", null, "Originally the .arpa TLD was short for Advanced Research Project Agency. This was the agency responsible for funding ARPANET, the first incarnation of the Internet.", -1)
const _hoisted_72 = /*#__PURE__*/_createElementVNode("p", null, "Many years later, the .arpa TLD became used almost exclusively for IP addresses in the form of PTR records. Since it was too late to rename the TLD, the Internet Engineering Task Force (IETF) decided to redefine the acronym. The TLD .arpa is now officially short for Address and Routing Parameter Area.", -1)

import BlogPost from '@/components/layout/BlogPost.vue';


import { useRouter } from 'vue-router';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import DomainSearch from '@/components/DomainSearch.vue';
import DnsRecordTypes from '@/views/learning/DnsRecordTypes.vue';
import CodeBlock from '@/components/micro/CodeBlock.vue';
import { learningBreadcrumb } from '@/model/Breadcrumb';
import Callout from '@/components/layout/CalloutComponent.vue';
import { domainNameToUrlPath } from '@/common/domainName';

export const meta: BlogMeta = {
  tag: 'DNS record types',
  url: '/learning/dns-record-types/ptr/',
  breadcrumbs: [learningBreadcrumb, DnsRecordTypes.breadcrumb],
  title: 'The PTR record',
  description: 'The PTR or "pointer" DNS record type maps an IP address to a domain name in the DNS. This is called a DNS reverse lookup.',
  overviewDescription: 'The PTR or "pointer" DNS record type maps an IP address to a domain name in the DNS. This is called a DNS reverse lookup.',
  overviewImage: '/img/learning/dns-record-types/ptr/the-dns-ptr-record-type-small.jpg',
  overviewImageAlt: 'Pointer records in the DNS',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/dns-record-types/ptr/the-ptr-record-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/dns-record-types/ptr/the-ptr-record-social.jpg',
  written: new Date('2022-09-02T09:08:52.860Z'),
  updated: new Date('2023-03-28T11:57:55.025Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'RecordTypePtr',
  setup(__props) {

const router = useRouter();

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/dns-records/ptr/`);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/dns-record-types/ptr/the-dns-ptr-record-type.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _hoisted_5,
        _hoisted_6,
        _hoisted_7,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_8
          ]),
          _: 1
        }),
        _hoisted_9,
        _hoisted_10,
        _hoisted_11,
        _hoisted_12,
        _hoisted_13,
        _hoisted_14,
        _hoisted_15,
        _hoisted_16,
        _createElementVNode("p", null, [
          _createTextVNode("Use a similar process to construct the name for an IPv6 record. Let's consider the example IPv6 address "),
          _hoisted_17,
          _createTextVNode(". As discussed in our article on "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/aaaa/" }, {
            default: _withCtx(() => [
              _createTextVNode("the AAAA record type")
            ]),
            _: 1
          }),
          _createTextVNode(", the two consecutive colons are a shortcut for enough zeros to pad the address out to 128 bits.")
        ]),
        _hoisted_18,
        _hoisted_19,
        _hoisted_20,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_21
          ]),
          _: 1
        }),
        _hoisted_22,
        _hoisted_23,
        _hoisted_24,
        _hoisted_25,
        _hoisted_26,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_27
          ]),
          _: 1
        }),
        _hoisted_28,
        _hoisted_29,
        _hoisted_30,
        _createVNode(Callout, {
          icon: "info",
          title: "Who manages DNS records in the .arpa TLD?",
          heading: "h2"
        }, {
          default: _withCtx(() => [
            _hoisted_31,
            _createElementVNode("p", null, [
              _createTextVNode("The IANA and the IAB do not manage individual PTR records. Child zones are "),
              _createVNode(_component_router_link, { to: "/learning/zone-delegation/" }, {
                default: _withCtx(() => [
                  _createTextVNode("delegated")
                ]),
                _: 1
              }),
              _createTextVNode(" below .arpa for blocks of IP addresses purchased by various agencies, ISPs, and corporations.")
            ]),
            _hoisted_32,
            _hoisted_33,
            _hoisted_34,
            _hoisted_35
          ]),
          _: 1
        }),
        _hoisted_36,
        _hoisted_37,
        _hoisted_38,
        _hoisted_39,
        _hoisted_40,
        _hoisted_41,
        _hoisted_42,
        _hoisted_43,
        _hoisted_44,
        _hoisted_45,
        _createElementVNode("p", null, [
          _createTextVNode("You can also check the PTR records for any domain name in our "),
          _createVNode(_component_router_link, { to: "/ptr-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("PTR lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" tool or by entering it here:")
        ]),
        _createElementVNode("form", _hoisted_46, [
          _createVNode(DomainSearch, {
            emphasize: "",
            "placeholder-text": "IP address or domain name",
            buttonText: "Find PTR record",
            onSubmitted: redirectTo
          })
        ]),
        _createElementVNode("div", _hoisted_47, [
          _hoisted_48,
          _createElementVNode("div", _hoisted_49, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_50,
                    _createElementVNode("span", _hoisted_51, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_52,
                    _createElementVNode("p", null, [
                      _createTextVNode("TTL selection in the DNS is often complicated. As we discussed in "),
                      _createVNode(_component_router_link, { to: "/learning/what-is-a-good-ttl-for-dns/" }, {
                        default: _withCtx(() => [
                          _createTextVNode("our article on TTL selection in the DNS")
                        ]),
                        _: 1
                      }),
                      _createTextVNode(", shorter TTLs give agility, but longer TTLs increase reliability. Longer TTLs can also decrease latency.")
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_53,
                    _createElementVNode("span", _hoisted_54, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_55,
                    _hoisted_56
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_57,
                    _createElementVNode("span", _hoisted_58, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_59,
                    _hoisted_60
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_61,
                    _createElementVNode("span", _hoisted_62, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_63,
                    _hoisted_64,
                    _hoisted_65,
                    _hoisted_66
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_67,
                    _createElementVNode("span", _hoisted_68, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_69,
                    _hoisted_70,
                    _hoisted_71,
                    _hoisted_72
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})